import { useQuery } from '@apollo/client/react';
import { useSetAtom } from 'jotai';
import { useEffect } from 'react';

import { BASE_DATE } from '~/constants/organization_notification';
import {
  GetUncompletedAppointmentsNotificationsCountDocument,
  GetUnreadEntriesNotificationsCountDocument,
  GetUnreadMedicationFollowupQuestionnaireSheetsDocument,
  GetUnreadTemporaryPermissionsDocument,
} from '~/graphql/generated/graphql';
import { getMe } from '~/graphql/utility';
import { useSubscriptions } from '~/hooks/use-subscriptions';
import { notificationState } from '~/state/layouts/SharedAppShell/atoms';

export const useSubscriptionNotifications = () => {
  const setNotificationState = useSetAtom(notificationState);

  const [organizationNotificationEvent] = useSubscriptions('OrganizationNotification', [
    'created',
    'updated',
  ]);
  const [medicationFollowupEvent] = useSubscriptions('MedicationFollowupQuestionnaireSheet', [
    'created',
    'updated',
  ]);
  const [medicineNoteTemporaryPermissionEvent] = useSubscriptions('HiccupTemporaryPermission', [
    'created',
  ]);

  const {
    loading: isFollowupLoading,
    refetch: refetchFollowup,
    data: followupData,
  } = useQuery(GetUnreadMedicationFollowupQuestionnaireSheetsDocument, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: { from: BASE_DATE },
  });

  const {
    loading: isNotificationLoading,
    refetch: refetchNotification,
    data: notificationData,
  } = useQuery(GetUncompletedAppointmentsNotificationsCountDocument, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: { from: BASE_DATE },
  });

  const {
    loading: isEntryNotificationLoading,
    refetch: refetchEntryNotification,
    data: entryNotificationData,
  } = useQuery(GetUnreadEntriesNotificationsCountDocument, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: { from: BASE_DATE },
  });

  const {
    loading: isMedicineNoteLoading,
    refetch: refetchMedicineNote,
    data: medicineNoteData,
  } = useQuery(GetUnreadTemporaryPermissionsDocument, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (!followupData) return;
    const count =
      getMe(followupData)?.organization.medicationFollowupQuestionnaireSheets.nodesCount || 0;
    setNotificationState((_state) => ({ ..._state, followupCount: count }));
  }, [followupData, setNotificationState]);

  useEffect(() => {
    if (!notificationData) return;
    setNotificationState((_state) => ({
      ..._state,
      receptionCount:
        getMe(notificationData)?.organization.organizationNotifications.nodesCount || 0,
    }));
  }, [notificationData, setNotificationState]);

  useEffect(() => {
    if (!entryNotificationData) return;
    setNotificationState((_state) => ({
      ..._state,
      entryCount:
        getMe(entryNotificationData)?.organization.organizationNotifications.nodesCount || 0,
    }));
  }, [entryNotificationData, setNotificationState]);

  useEffect(() => {
    if (!medicineNoteData) return;
    const count = getMe(medicineNoteData)?.organization.hiccupTemporaryPermissions.nodesCount || 0;
    setNotificationState((_state) => ({ ..._state, medicineNoteCount: count }));
  }, [medicineNoteData, setNotificationState]);

  useEffect(() => {
    if (
      medicationFollowupEvent?.actionName === 'created' ||
      medicationFollowupEvent?.actionName === 'updated'
    ) {
      refetchFollowup();
    }
  }, [medicationFollowupEvent, refetchFollowup]);

  useEffect(() => {
    if (
      organizationNotificationEvent?.actionName === 'created' ||
      organizationNotificationEvent?.actionName === 'updated'
    ) {
      refetchEntryNotification();
      refetchNotification();
    }
  }, [organizationNotificationEvent, refetchNotification, refetchEntryNotification]);

  useEffect(() => {
    if (medicineNoteTemporaryPermissionEvent?.actionName === 'created') {
      refetchMedicineNote();
    }
  }, [medicineNoteTemporaryPermissionEvent, refetchMedicineNote]);

  useEffect(() => {
    refetchEntryNotification();
    refetchFollowup();
    refetchNotification();
    refetchMedicineNote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    loading:
      isFollowupLoading ||
      isNotificationLoading ||
      isEntryNotificationLoading ||
      isMedicineNoteLoading,
  };
};
